/**
 * VenueOwnerDashboard - Purpose-built dashboard for venue owners.
 *
 * "The experience should feel approachable — less developer tool,
 * more business profile manager — while reusing the same underlying
 * components that admins use." — PRD v1.1
 *
 * Sections:
 *   1. Venue Profile — view/edit venue info
 *   2. Upload Content — scoped photo/text upload (reuse existing admin tools)
 *   3. My Content — published deals, events, specials
 *   4. Analytics — tier-gated (teaser for free, full for paid)
 *
 * This is a separate component from AdminConsole (3000+ lines) to keep
 * things manageable and give venue owners a distinct UX.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useMarketNavigate } from '../hooks/useMarketNavigate';
import { MarketLink } from '../components/MarketLink';
import { useMarket } from '../context/MarketContext';
import { useAuth } from '../context/AuthContext';
import SEOHead from '../components/SEOHead';
import { StatCard, AnalyticsLineChart, HeatmapChart } from '../components/analytics';
import './VenueOwnerDashboard.css';

// Backend API base URL
const API_BASE = process.env.REACT_APP_API_URL || '';

function VenueOwnerDashboard() {
  const navigate = useMarketNavigate();
  const market = useMarket();
  const { user, logout: authLogout, getToken } = useAuth();

  // --- State ---
  const [venues, setVenues] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState(null);
  const [venuesLoading, setVenuesLoading] = useState(true);
  const [venuesError, setVenuesError] = useState(null);

  const [activeTab, setActiveTab] = useState('profile');

  // My Content state
  const [content, setContent] = useState({ deals: [], events: [], specials: [] });
  const [contentLoading, setContentLoading] = useState(false);

  // Profile edit state
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState(null);

  // Thumbnail upload state
  const [thumbnailUploading, setThumbnailUploading] = useState(false);

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);

  // Analytics state (Airtable-based)
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // GA4 engagement state
  const [ga4Engagement, setGa4Engagement] = useState(null);
  const [ga4EngagementLoading, setGa4EngagementLoading] = useState(false);
  const [ga4Heatmap, setGa4Heatmap] = useState(null);
  const [ga4HeatmapLoading, setGa4HeatmapLoading] = useState(false);

  // Tier logic: Basic (free) / Silver / Platinum
  const TIER_LEVELS = { 'basic': 0, 'free': 0, 'silver': 1, 'platinum': 2, 'premium': 2 };
  const userTierLevel = (user?.role === 'admin' || user?.role === 'super_admin')
    ? 2 // admins see everything
    : (TIER_LEVELS[(user?.accountTier || user?.account_tier || '').toLowerCase()] ?? 0);
  const isSilverPlus = userTierLevel >= 1;
  const isPlatinum = userTierLevel >= 2;
  const isPremium = isSilverPlus; // backward compat for existing sections

  // Upload state
  const [uploadMode, setUploadMode] = useState('photos'); // 'photos' or 'text'
  const [pendingFiles, setPendingFiles] = useState([]);
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [selectedExtractions, setSelectedExtractions] = useState({});
  const [pastedText, setPastedText] = useState('');
  const [isProcessingText, setIsProcessingText] = useState(false);
  const [textExtractedData, setTextExtractedData] = useState(null);
  const [textSelectedExtractions, setTextSelectedExtractions] = useState({});
  const [uploadMessage, setUploadMessage] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Content request modal state
  const [requestModal, setRequestModal] = useState(null); // { type: 'edit'|'removal', contentType, contentId, contentName }
  const [requestNotes, setRequestNotes] = useState('');
  const [requestSubmitting, setRequestSubmitting] = useState(false);
  const [requestMessage, setRequestMessage] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [originalFormData, setOriginalFormData] = useState({});

  // Add New content modal state (Silver+ only)
  const [addNewModal, setAddNewModal] = useState(false);
  const [addNewContentType, setAddNewContentType] = useState(null); // 'deal' | 'event' | 'special'
  const [addNewFormData, setAddNewFormData] = useState({});
  const [addNewNotes, setAddNewNotes] = useState('');
  const [addNewSubmitting, setAddNewSubmitting] = useState(false);
  const [addNewMessage, setAddNewMessage] = useState(null);
  const [addNewImageFile, setAddNewImageFile] = useState(null); // event flyer image (Silver+)

  // Upgrade request modal state
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [upgradeMessage, setUpgradeMessage] = useState('');
  const [upgradeSubmitting, setUpgradeSubmitting] = useState(false);
  const [upgradeResult, setUpgradeResult] = useState(null); // { type: 'success'|'error', text }

  // Getting started dismissed
  const [gettingStartedDismissed, setGettingStartedDismissed] = useState(
    () => localStorage.getItem('vd_getting_started_dismissed') === 'true'
  );

  const baseId = market.airtableBaseId;

  // --- Auth Headers ---
  const getAuthHeaders = useCallback((contentType = 'application/json') => {
    const headers = {};
    if (contentType) headers['Content-Type'] = contentType;
    const token = getToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    return headers;
  }, [getToken]);

  // --- Fetch Venues ---
  const fetchVenues = useCallback(async () => {
    setVenuesLoading(true);
    setVenuesError(null);
    try {
      const res = await fetch(`${API_BASE}/api/my-venues?baseId=${baseId}`, {
        headers: getAuthHeaders(null),
      });
      if (!res.ok) throw new Error('Failed to load venues');
      const data = await res.json();
      setVenues(data.venues || []);

      // Auto-select first venue (or only venue)
      if (data.venues?.length > 0) {
        setSelectedVenueId(prev => prev || data.venues[0].id);
      }
    } catch (err) {
      console.error('Fetch venues error:', err);
      setVenuesError(err.message);
    } finally {
      setVenuesLoading(false);
    }
  }, [baseId, getAuthHeaders]);

  useEffect(() => {
    fetchVenues();
  }, [fetchVenues]);

  // --- Fetch Content for Selected Venue ---
  const fetchContent = useCallback(async () => {
    if (!selectedVenueId) return;
    setContentLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/my-content?baseId=${baseId}&venueId=${selectedVenueId}`,
        { headers: getAuthHeaders(null) }
      );
      if (!res.ok) throw new Error('Failed to load content');
      const data = await res.json();
      setContent({
        deals: data.deals || [],
        events: data.events || [],
        specials: data.specials || [],
      });
    } catch (err) {
      console.error('Fetch content error:', err);
    } finally {
      setContentLoading(false);
    }
  }, [selectedVenueId, baseId, getAuthHeaders]);

  useEffect(() => {
    if (activeTab === 'content') {
      fetchContent();
    }
  }, [activeTab, fetchContent]);

  // --- Fetch Submissions ---
  const fetchSubmissions = useCallback(async () => {
    if (!selectedVenueId) return;
    setSubmissionsLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/my-submissions?baseId=${baseId}&venueId=${selectedVenueId}`,
        { headers: getAuthHeaders(null) }
      );
      if (res.ok) {
        const data = await res.json();
        setSubmissions(data.submissions || []);
      }
    } catch (err) {
      console.error('Fetch submissions error:', err);
    } finally {
      setSubmissionsLoading(false);
    }
  }, [selectedVenueId, baseId, getAuthHeaders]);

  useEffect(() => {
    if (activeTab === 'content') {
      fetchSubmissions();
    }
  }, [activeTab, fetchSubmissions]);

  // --- Fetch Analytics ---
  const fetchAnalytics = useCallback(async () => {
    if (!selectedVenueId) return;
    setAnalyticsLoading(true);
    try {
      const res = await fetch(
        `${API_BASE}/api/venues/${selectedVenueId}/analytics?baseId=${baseId}`,
        { headers: getAuthHeaders(null) }
      );
      if (res.ok) {
        const data = await res.json();
        setAnalytics(data);
      }
    } catch (err) {
      console.error('Fetch analytics error:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  }, [selectedVenueId, baseId, getAuthHeaders]);

  // --- Fetch GA4 Engagement ---
  const fetchGa4Engagement = useCallback(async () => {
    if (!selectedVenueId) return;
    const selectedVenue = venues.find(v => v.id === selectedVenueId);
    if (!selectedVenue?.name) return;

    const venueName = encodeURIComponent(selectedVenue.name);
    const period = isSilverPlus ? '30d' : '7d';
    const headers = getAuthHeaders(null);

    // Basic tier: just engagement totals
    setGa4EngagementLoading(true);
    fetch(`${API_BASE}/api/analytics/venue-engagement?venueName=${venueName}&period=${period}`, { headers })
      .then(r => r.ok ? r.json() : null)
      .then(d => d && setGa4Engagement(d))
      .catch(() => {})
      .finally(() => setGa4EngagementLoading(false));

    // Silver+: day-of-week heatmap
    if (isSilverPlus) {
      setGa4HeatmapLoading(true);
      fetch(`${API_BASE}/api/analytics/venue-heatmap?venueName=${venueName}&period=30d`, { headers })
        .then(r => r.ok ? r.json() : null)
        .then(d => d && setGa4Heatmap(d))
        .catch(() => {})
        .finally(() => setGa4HeatmapLoading(false));
    }
  }, [selectedVenueId, venues, isSilverPlus, getAuthHeaders]);

  useEffect(() => {
    if (activeTab === 'analytics') {
      fetchAnalytics();
      fetchGa4Engagement();
    }
  }, [activeTab, fetchAnalytics, fetchGa4Engagement]);

  // --- Selected Venue ---
  const selectedVenue = venues.find(v => v.id === selectedVenueId);

  // --- Profile Form ---
  useEffect(() => {
    if (selectedVenue) {
      setProfileForm({
        address: selectedVenue.address || '',
        city: selectedVenue.city || '',
        phone: selectedVenue.phone || '',
        website: selectedVenue.website || '',
        type: selectedVenue.type || '',
        description: selectedVenue.description || '',
        facebookUrl: selectedVenue.facebookUrl || '',
        instagramUrl: selectedVenue.instagramUrl || '',
      });
    }
  }, [selectedVenue]);

  const handleProfileSave = async () => {
    if (!selectedVenueId) return;
    setProfileSaving(true);
    setProfileMessage(null);
    try {
      const res = await fetch(`${API_BASE}/api/venues/${selectedVenueId}/profile`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...profileForm, baseId }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }
      setProfileMessage({ type: 'success', text: 'Profile updated successfully!' });
      setIsEditingProfile(false);
      // Refresh venues to pick up changes
      await fetchVenues();
    } catch (err) {
      setProfileMessage({ type: 'error', text: err.message });
    } finally {
      setProfileSaving(false);
    }
  };

  // --- Thumbnail Upload ---
  const handleThumbnailUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !selectedVenueId) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      setProfileMessage({ type: 'error', text: 'Please select a JPG, PNG, WebP, or GIF image.' });
      return;
    }

    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      setProfileMessage({ type: 'error', text: 'Image must be under 5MB.' });
      return;
    }

    setThumbnailUploading(true);
    setProfileMessage(null);
    try {
      const formData = new FormData();
      formData.append('thumbnail', file);
      formData.append('baseId', baseId);

      const res = await fetch(`${API_BASE}/api/venues/${selectedVenueId}/thumbnail`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to upload image');
      }
      setProfileMessage({ type: 'success', text: 'Venue photo updated!' });
      // Refresh venues to show new thumbnail
      await fetchVenues();
    } catch (err) {
      setProfileMessage({ type: 'error', text: err.message });
    } finally {
      setThumbnailUploading(false);
      // Reset the file input so the same file can be re-selected
      e.target.value = '';
    }
  };

  // --- Photo Upload Handlers ---
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setPendingFiles(prev => [...prev, ...files]);
    }
    e.target.value = '';
  };

  const handleRemoveFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleProcessPhotos = async () => {
    if (!selectedVenueId || pendingFiles.length === 0) return;
    setIsProcessingPhotos(true);
    setUploadMessage(null);
    setExtractedData(null);

    try {
      const formData = new FormData();
      formData.append('restaurantId', selectedVenueId);
      formData.append('restaurantName', selectedVenue.name);
      formData.append('baseId', baseId);
      pendingFiles.forEach(file => formData.append('photos', file));

      const res = await fetch(`${API_BASE}/api/process-photos`, {
        method: 'POST',
        headers: getAuthHeaders(null),
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to process photos');
      }

      const result = await res.json();
      const data = result.data || result;

      const extractionResult = {
        restaurantId: selectedVenueId,
        restaurantName: selectedVenue.name,
        happyHours: data.happyHours || [],
        events: data.events || [],
        specials: data.specials || [],
      };

      setExtractedData(extractionResult);

      // Select all by default
      const selections = {};
      (extractionResult.happyHours || []).forEach((_, i) => { selections[`hh-${i}`] = true; });
      (extractionResult.events || []).forEach((_, i) => { selections[`event-${i}`] = true; });
      (extractionResult.specials || []).forEach((_, i) => { selections[`special-${i}`] = true; });
      setSelectedExtractions(selections);

      if (extractionResult.happyHours.length === 0 && extractionResult.events.length === 0 && extractionResult.specials.length === 0) {
        setUploadMessage({ type: 'info', text: 'No deals, events, or specials were detected in the photos. Try a clearer image or use text upload instead.' });
      }
    } catch (err) {
      console.error('Process photos error:', err);
      setUploadMessage({ type: 'error', text: err.message });
    } finally {
      setIsProcessingPhotos(false);
    }
  };

  const handleToggleExtraction = (key) => {
    setSelectedExtractions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveExtractions = async () => {
    if (!extractedData) return;
    setIsSaving(true);
    setUploadMessage(null);

    try {
      const payload = {
        extractions: [{
          restaurantId: extractedData.restaurantId,
          restaurantName: selectedVenue?.name || '',
          happyHours: (extractedData.happyHours || []).filter((_, i) => selectedExtractions[`hh-${i}`]),
          events: (extractedData.events || []).filter((_, i) => selectedExtractions[`event-${i}`]),
          specials: (extractedData.specials || []).filter((_, i) => selectedExtractions[`special-${i}`]),
        }].filter(r => r.happyHours.length > 0 || r.events.length > 0 || r.specials.length > 0),
        baseId,
      };

      if (payload.extractions.length === 0) {
        setUploadMessage({ type: 'error', text: 'No items selected to save.' });
        setIsSaving(false);
        return;
      }

      const res = await fetch(`${API_BASE}/api/save-extractions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }

      const result = await res.json();
      if (result.pending) {
        setUploadMessage({ type: 'success', text: 'Content submitted for review! An admin will review your submission shortly.' });
      } else {
        setUploadMessage({ type: 'success', text: `Content saved successfully! ${result.savedCount || ''} items published.` });
      }
      setExtractedData(null);
      setSelectedExtractions({});
      setPendingFiles([]);
      // Refresh submissions if on content tab
      if (activeTab === 'content') fetchSubmissions();
    } catch (err) {
      console.error('Save extractions error:', err);
      setUploadMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearPhotoUpload = () => {
    setPendingFiles([]);
    setExtractedData(null);
    setSelectedExtractions({});
    setUploadMessage(null);
  };

  // --- Text Upload Handlers ---
  const handleProcessText = async () => {
    if (!selectedVenueId || !pastedText.trim()) return;
    setIsProcessingText(true);
    setUploadMessage(null);
    setTextExtractedData(null);

    try {
      const res = await fetch(`${API_BASE}/api/process-text`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          restaurantId: selectedVenueId,
          restaurantName: selectedVenue.name,
          text: pastedText,
          baseId,
        }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to process text');
      }

      const result = await res.json();
      const data = result.data || result;

      const extractionResult = {
        restaurantId: selectedVenueId,
        restaurantName: selectedVenue.name,
        happyHours: data.happyHours || [],
        events: data.events || [],
        specials: data.specials || [],
      };

      setTextExtractedData(extractionResult);

      const selections = {};
      (extractionResult.happyHours || []).forEach((_, i) => { selections[`hh-${i}`] = true; });
      (extractionResult.events || []).forEach((_, i) => { selections[`event-${i}`] = true; });
      (extractionResult.specials || []).forEach((_, i) => { selections[`special-${i}`] = true; });
      setTextSelectedExtractions(selections);

      if (extractionResult.happyHours.length === 0 && extractionResult.events.length === 0 && extractionResult.specials.length === 0) {
        setUploadMessage({ type: 'info', text: 'No deals, events, or specials were detected in the text. Try including more details about times, dates, or prices.' });
      }
    } catch (err) {
      console.error('Process text error:', err);
      setUploadMessage({ type: 'error', text: err.message });
    } finally {
      setIsProcessingText(false);
    }
  };

  const handleToggleTextExtraction = (key) => {
    setTextSelectedExtractions(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const handleSaveTextExtractions = async () => {
    if (!textExtractedData) return;
    setIsSaving(true);
    setUploadMessage(null);

    try {
      const payload = {
        extractions: [{
          restaurantId: textExtractedData.restaurantId,
          restaurantName: selectedVenue?.name || '',
          source: 'social://text-upload',
          happyHours: (textExtractedData.happyHours || []).filter((_, i) => textSelectedExtractions[`hh-${i}`]),
          events: (textExtractedData.events || []).filter((_, i) => textSelectedExtractions[`event-${i}`]),
          specials: (textExtractedData.specials || []).filter((_, i) => textSelectedExtractions[`special-${i}`]),
        }].filter(r => r.happyHours.length > 0 || r.events.length > 0 || r.specials.length > 0),
        baseId,
      };

      if (payload.extractions.length === 0) {
        setUploadMessage({ type: 'error', text: 'No items selected to save.' });
        setIsSaving(false);
        return;
      }

      const res = await fetch(`${API_BASE}/api/save-extractions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to save');
      }

      const result = await res.json();
      if (result.pending) {
        setUploadMessage({ type: 'success', text: 'Content submitted for review! An admin will review your submission shortly.' });
      } else {
        setUploadMessage({ type: 'success', text: `Content saved successfully! ${result.savedCount || ''} items published.` });
      }
      setTextExtractedData(null);
      setTextSelectedExtractions({});
      setPastedText('');
      if (activeTab === 'content') fetchSubmissions();
    } catch (err) {
      console.error('Save text extractions error:', err);
      setUploadMessage({ type: 'error', text: err.message });
    } finally {
      setIsSaving(false);
    }
  };

  const handleClearTextUpload = () => {
    setPastedText('');
    setTextExtractedData(null);
    setTextSelectedExtractions({});
    setUploadMessage(null);
  };

  // --- Extraction Review Renderer (shared between photo and text) ---
  const renderExtractionReview = (data, selections, toggleFn, saveFn, clearFn) => {
    const totalItems = (data.happyHours?.length || 0) + (data.events?.length || 0) + (data.specials?.length || 0);
    const selectedCount = Object.values(selections).filter(Boolean).length;

    return (
      <div className="vd-extraction-review">
        <div className="vd-extraction-header">
          <h4>Review Extracted Content</h4>
          <span className="vd-extraction-count">{selectedCount} of {totalItems} selected</span>
        </div>

        {data.happyHours?.length > 0 && (
          <div className="vd-extraction-group">
            <h5>Happy Hour Deals ({data.happyHours.length})</h5>
            {data.happyHours.map((item, i) => (
              <label key={`hh-${i}`} className="vd-extraction-item">
                <input
                  type="checkbox"
                  checked={!!selections[`hh-${i}`]}
                  onChange={() => toggleFn(`hh-${i}`)}
                />
                <div className="vd-extraction-details">
                  <strong>{item.name || item.deal_name || 'Untitled Deal'}</strong>
                  {item.description && <p>{item.description}</p>}
                  {(item.time || item.days) && (
                    <span className="vd-extraction-meta">{item.days} {item.time}</span>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {data.events?.length > 0 && (
          <div className="vd-extraction-group">
            <h5>Events ({data.events.length})</h5>
            {data.events.map((item, i) => (
              <label key={`event-${i}`} className="vd-extraction-item">
                <input
                  type="checkbox"
                  checked={!!selections[`event-${i}`]}
                  onChange={() => toggleFn(`event-${i}`)}
                />
                <div className="vd-extraction-details">
                  <strong>{item.name || item.event_name || 'Untitled Event'}</strong>
                  {item.description && <p>{item.description}</p>}
                  {(item.date || item.type) && (
                    <span className="vd-extraction-meta">{item.type} {item.date}</span>
                  )}
                </div>
              </label>
            ))}
          </div>
        )}

        {data.specials?.length > 0 && (
          <div className="vd-extraction-group">
            <h5>Specials ({data.specials.length})</h5>
            {data.specials.map((item, i) => (
              <label key={`special-${i}`} className="vd-extraction-item">
                <input
                  type="checkbox"
                  checked={!!selections[`special-${i}`]}
                  onChange={() => toggleFn(`special-${i}`)}
                />
                <div className="vd-extraction-details">
                  <strong>{item.name || item.special_name || 'Untitled Special'}</strong>
                  {item.description && <p>{item.description}</p>}
                  {item.days && <span className="vd-extraction-meta">{item.days}</span>}
                </div>
              </label>
            ))}
          </div>
        )}

        <div className="vd-extraction-actions">
          <button
            className="vd-save-button"
            onClick={saveFn}
            disabled={isSaving || selectedCount === 0}
          >
            {isSaving ? 'Saving...' : `Save ${selectedCount} Item${selectedCount !== 1 ? 's' : ''} to Database`}
          </button>
          <button className="vd-cancel-button" onClick={clearFn}>
            Discard & Start Over
          </button>
        </div>
      </div>
    );
  };

  // --- Content Request Modal ---
  const ALL_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const EVENT_TYPES = ['Live Music', 'Trivia', 'Karaoke', 'Comedy', 'Open Mic', 'Sports', 'Special Event', 'Holiday', 'DJ', 'Bingo'];

  // Generate time options in 30-min increments starting at 8 AM (12-hr format)
  const TIME_OPTIONS = (() => {
    const opts = [];
    for (let h = 8; h < 24; h++) {
      for (let m = 0; m < 60; m += 30) {
        const hour12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
        const ampm = h < 12 ? 'AM' : 'PM';
        const min = m.toString().padStart(2, '0');
        opts.push(`${hour12}:${min} ${ampm}`);
      }
    }
    return opts;
  })();

  // Check if a content item has an active (pending or rejected) request
  const getActiveRequest = (contentId) =>
    submissions.find(s =>
      s.extractedData?.originalContentId === contentId &&
      (s.status === 'pending' || s.status === 'rejected')
    );

  // Parse Days of Week from Airtable (comma-separated string or array) into an array
  const parseDays = (val) => {
    if (!val) return [];
    if (Array.isArray(val)) return val;
    return val.split(',').map(d => d.trim()).filter(Boolean);
  };

  const openRequestModal = (type, contentType, contentId, contentName) => {
    setRequestModal({ type, contentType, contentId, contentName });
    setRequestNotes('');
    setRequestMessage(null);

    if (type === 'edit') {
      let formData = {};
      if (contentType === 'deal') {
        const item = content.deals.find(d => d.id === contentId);
        formData = {
          'Deal Name': item?.['Deal Name'] || '',
          'Description': item?.['Description'] || '',
          'Start Time': item?.['Start Time'] || '',
          'End Time': item?.['End Time'] || '',
          'Days of Week': parseDays(item?.['Days of Week']),
        };
      } else if (contentType === 'event') {
        const item = content.events.find(e => e.id === contentId);
        const isRecurring = !!item?.['Recurring'];
        formData = {
          'Event Name': item?.['Event Name'] || '',
          'Description': item?.['Description'] || '',
          'Event Type': item?.['Event Type'] || '',
          'Recurring': isRecurring,
          'Days of Week': parseDays(item?.['Days of Week']),
          'Start Time': item?.['Start Time'] || '',
          'End Time': item?.['End Time'] || '',
          'Event Start': item?.['Event Start'] ? item['Event Start'].slice(0, 16) : '', // datetime-local format
        };
      } else if (contentType === 'special') {
        const item = content.specials.find(s => s.id === contentId);
        formData = {
          'Special Name': item?.['Special Name'] || '',
          'Description': item?.['Description'] || '',
          'Days of Week': parseDays(item?.['Days of Week']),
        };
      }
      setEditFormData({ ...formData });
      setOriginalFormData({ ...formData });
    } else {
      setEditFormData({});
      setOriginalFormData({});
    }
  };

  const closeRequestModal = () => {
    setRequestModal(null);
    setRequestNotes('');
    setRequestMessage(null);
  };

  const handleSubmitRequest = async () => {
    if (!requestModal) return;

    if (requestModal.type === 'edit') {
      // Compute changed fields
      const changedFields = {};
      const origFields = {};
      for (const [key, newValue] of Object.entries(editFormData)) {
        const origValue = originalFormData[key];
        if (JSON.stringify(newValue) !== JSON.stringify(origValue)) {
          changedFields[key] = newValue;
          origFields[key] = origValue;
        }
      }
      const hasFieldChanges = Object.keys(changedFields).length > 0;
      if (!hasFieldChanges && !requestNotes.trim()) {
        setRequestMessage({ type: 'error', text: 'Modify at least one field or add a note.' });
        return;
      }
    }

    setRequestSubmitting(true);
    setRequestMessage(null);

    try {
      let bodyPayload = {
        baseId,
        contentType: requestModal.contentType,
        contentId: requestModal.contentId,
        contentName: requestModal.contentName,
        requestType: requestModal.type,
        notes: requestNotes.trim(),
      };

      if (requestModal.type === 'edit') {
        // Compute changed fields for the request body
        const changedFields = {};
        const origFields = {};
        for (const [key, newValue] of Object.entries(editFormData)) {
          const origValue = originalFormData[key];
          if (JSON.stringify(newValue) !== JSON.stringify(origValue)) {
            changedFields[key] = newValue;
            origFields[key] = origValue;
          }
        }
        if (Object.keys(changedFields).length > 0) {
          bodyPayload.editedFields = changedFields;
          bodyPayload.originalFields = origFields;
        }
      }

      const res = await fetch(`${API_BASE}/api/my-content/request`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(bodyPayload),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to submit request');
      }

      setRequestMessage({
        type: 'success',
        text: requestModal.type === 'edit'
          ? 'Edit request submitted! An admin will review it shortly.'
          : 'Removal request submitted! An admin will review it shortly.',
      });

      // Close after brief delay so user sees the success message
      setTimeout(() => {
        closeRequestModal();
        fetchSubmissions();
      }, 1500);

    } catch (err) {
      console.error('Submit request error:', err);
      setRequestMessage({ type: 'error', text: err.message });
    } finally {
      setRequestSubmitting(false);
    }
  };

  // --- Getting Started ---
  const dismissGettingStarted = () => {
    setGettingStartedDismissed(true);
    localStorage.setItem('vd_getting_started_dismissed', 'true');
  };

  // --- Add New Content Modal ---
  const openAddNewModal = () => {
    setAddNewModal(true);
    setAddNewContentType(null);
    setAddNewFormData({});
    setAddNewNotes('');
    setAddNewMessage(null);
    setAddNewImageFile(null);
  };

  const closeAddNewModal = () => {
    setAddNewModal(false);
    setAddNewContentType(null);
    setAddNewFormData({});
    setAddNewNotes('');
    setAddNewMessage(null);
    setAddNewImageFile(null);
  };

  const selectAddNewContentType = (type) => {
    setAddNewContentType(type);
    setAddNewMessage(null);
    if (type === 'deal') {
      setAddNewFormData({
        'Deal Name': '',
        'Description': '',
        'Start Time': '',
        'End Time': '',
        'Days of Week': [],
      });
    } else if (type === 'event') {
      setAddNewFormData({
        'Event Name': '',
        'Description': '',
        'Event Type': '',
        'Recurring': false,
        'Days of Week': [],
        'Start Time': '',
        'End Time': '',
        'Event Start': '',
      });
    } else if (type === 'special') {
      setAddNewFormData({
        'Special Name': '',
        'Description': '',
        'Days of Week': [],
      });
    }
  };

  const handleAddNewSubmit = async () => {
    if (!addNewContentType || !selectedVenueId) return;

    // Validate required fields
    const nameField = addNewContentType === 'deal' ? 'Deal Name'
      : addNewContentType === 'event' ? 'Event Name' : 'Special Name';
    if (!addNewFormData[nameField]?.trim()) {
      setAddNewMessage({ type: 'error', text: `Please enter a ${nameField.toLowerCase()}.` });
      return;
    }

    setAddNewSubmitting(true);
    setAddNewMessage(null);

    try {
      // Build form data (supports optional image for events)
      const formData = new FormData();
      formData.append('baseId', baseId);
      formData.append('contentType', addNewContentType);
      formData.append('venueId', selectedVenueId);
      formData.append('fields', JSON.stringify(addNewFormData));
      if (addNewNotes.trim()) {
        formData.append('notes', addNewNotes.trim());
      }
      if (addNewImageFile) {
        formData.append('image', addNewImageFile);
      }

      const res = await fetch(`${API_BASE}/api/my-content/add-new`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${getToken()}` },
        body: formData,
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to submit new content');
      }

      setAddNewMessage({
        type: 'success',
        text: 'New content submitted for review! An admin will review it shortly.',
      });

      // Close after brief delay
      setTimeout(() => {
        closeAddNewModal();
        fetchSubmissions();
      }, 1500);
    } catch (err) {
      console.error('Add new content error:', err);
      setAddNewMessage({ type: 'error', text: err.message });
    } finally {
      setAddNewSubmitting(false);
    }
  };

  // --- Upgrade Request ---
  const openUpgradeModal = () => {
    setUpgradeModal(true);
    setUpgradeMessage('');
    setUpgradeResult(null);
  };

  const closeUpgradeModal = () => {
    setUpgradeModal(false);
    setUpgradeMessage('');
    setUpgradeResult(null);
  };

  const handleUpgradeSubmit = async () => {
    if (!selectedVenue || !user) return;
    setUpgradeSubmitting(true);
    setUpgradeResult(null);

    try {
      const res = await fetch(`${API_BASE}/api/submissions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          baseId,
          type: 'upgrade_request',
          name: user.name || '',
          email: user.email || '',
          venue_name: selectedVenue.name || '',
          venue_id: selectedVenueId,
          user_id: user.id,
          current_tier: (user.accountTier || user.account_tier || 'free').toLowerCase(),
          requested_tier: 'silver',
          message: upgradeMessage.trim() || '',
        }),
      });

      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.error || 'Submission failed');
      }

      setUpgradeResult({ type: 'success', text: 'Upgrade request submitted! Our team will review it shortly.' });
      setTimeout(() => closeUpgradeModal(), 2500);
    } catch (err) {
      console.error('Upgrade request error:', err);
      setUpgradeResult({ type: 'error', text: err.message || 'Something went wrong. Please try again.' });
    } finally {
      setUpgradeSubmitting(false);
    }
  };

  // --- Logout ---
  const handleLogout = async () => {
    await authLogout();
    navigate('/login');
  };

  // --- Profile Completeness ---
  const getProfileCompleteness = () => {
    if (!selectedVenue) return { percent: 0, missing: [] };
    const fields = [
      { key: 'address', label: 'Address' },
      { key: 'city', label: 'City' },
      { key: 'phone', label: 'Phone' },
      { key: 'website', label: 'Website' },
      { key: 'type', label: 'Venue Type' },
      { key: 'description', label: 'Description' },
      { key: 'facebookUrl', label: 'Facebook URL' },
      { key: 'instagramUrl', label: 'Instagram URL' },
    ];
    const filled = fields.filter(f => selectedVenue[f.key] && selectedVenue[f.key].toString().trim());
    const missing = fields.filter(f => !selectedVenue[f.key] || !selectedVenue[f.key].toString().trim());
    const percent = Math.round((filled.length / fields.length) * 100);
    return { percent, missing: missing.map(f => f.label) };
  };

  const profileCompleteness = getProfileCompleteness();

  // --- Render ---
  return (
    <div className="venue-dashboard">
      <SEOHead title="Venue Dashboard" noindex market={market} />
      {/* Header */}
      <header className="vd-header">
        <div className="vd-header-content">
          <div className="vd-header-left">
            <div className="vd-header-text">
              <h1>Venue Dashboard</h1>
              <p className="vd-header-user">
                {user?.name || user?.email}
                <span className="vd-role-badge">Venue Owner</span>
              </p>
            </div>
          </div>
          <div className="vd-header-actions">
            <MarketLink to="/" className="vd-back-button">
              ← Back to Site
            </MarketLink>
            <button onClick={handleLogout} className="vd-logout-button">
              Logout
            </button>
          </div>
        </div>
      </header>

      <div className="vd-container">
        {/* Venue Selector (only show if multiple venues) */}
        {venues.length > 1 && (
          <div className="vd-venue-selector">
            <label>Select Venue:</label>
            <select
              value={selectedVenueId || ''}
              onChange={(e) => setSelectedVenueId(e.target.value)}
            >
              {venues.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
          </div>
        )}

        {/* Loading State */}
        {venuesLoading && (
          <div className="vd-loading">
            <div className="vd-spinner"></div>
            <p>Loading your venues...</p>
          </div>
        )}

        {/* Error State */}
        {venuesError && (
          <div className="vd-error">
            <p>Failed to load venues: {venuesError}</p>
            <button onClick={fetchVenues} className="vd-retry-button">Try Again</button>
          </div>
        )}

        {/* No Venues State */}
        {!venuesLoading && !venuesError && venues.length === 0 && (
          <div className="vd-empty">
            <div className="vd-empty-icon">🏪</div>
            <h2>No Venues Assigned</h2>
            <p>You don't have any venues linked to your account yet. Contact your admin to get set up.</p>
          </div>
        )}

        {/* Main Dashboard */}
        {selectedVenue && (
          <>
            {/* Venue Name Banner */}
            <div className="vd-venue-banner">
              <div
                className="vd-thumbnail-wrapper"
                onClick={() => document.getElementById('thumbnail-upload').click()}
                title="Click to change venue photo"
              >
                {selectedVenue.thumbnailImage?.[0]?.thumbnails?.large?.url ? (
                  <img
                    className="vd-venue-thumbnail"
                    src={selectedVenue.thumbnailImage[0].thumbnails.large.url}
                    alt={selectedVenue.name}
                  />
                ) : (
                  <div className="vd-venue-thumbnail vd-thumbnail-placeholder">
                    <span>📷</span>
                  </div>
                )}
                <div className={`vd-thumbnail-overlay${thumbnailUploading ? ' uploading' : ''}`}>
                  {thumbnailUploading ? '⏳' : '📷'}
                </div>
                <input
                  id="thumbnail-upload"
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  onChange={handleThumbnailUpload}
                  style={{ display: 'none' }}
                />
              </div>
              <div className="vd-venue-banner-text">
                <h2>{selectedVenue.name}</h2>
                <span className="vd-venue-city">{selectedVenue.city}</span>
              </div>
            </div>

            {/* Getting Started Card */}
            {!gettingStartedDismissed && (
              <div className="vd-getting-started">
                <div className="vd-gs-header">
                  <h3>Welcome to your Venue Dashboard</h3>
                  <button className="vd-gs-dismiss" onClick={dismissGettingStarted} title="Dismiss">&times;</button>
                </div>
                <p className="vd-gs-subtitle">Here's how to get the most out of your listing:</p>
                <div className="vd-gs-steps">
                  <button className="vd-gs-step" onClick={() => setActiveTab('profile')}>
                    <span className="vd-gs-step-icon">1</span>
                    <div>
                      <strong>Complete your venue profile</strong>
                      <span>Add your address, phone, description, and photos</span>
                    </div>
                  </button>
                  <button className="vd-gs-step" onClick={() => setActiveTab('upload')}>
                    <span className="vd-gs-step-icon">2</span>
                    <div>
                      <strong>Upload your happy hour deals & events</strong>
                      <span>Use photos or text — our AI extracts the details</span>
                    </div>
                  </button>
                  <button className="vd-gs-step" onClick={() => setActiveTab('content')}>
                    <span className="vd-gs-step-icon">3</span>
                    <div>
                      <strong>Review your published content</strong>
                      <span>See what's live and request changes anytime</span>
                    </div>
                  </button>
                </div>
              </div>
            )}

            {/* Tab Navigation */}
            <nav className="vd-tabs">
              <button
                className={`vd-tab ${activeTab === 'profile' ? 'active' : ''}`}
                onClick={() => setActiveTab('profile')}
              >
                Venue Profile
              </button>
              <button
                className={`vd-tab ${activeTab === 'content' ? 'active' : ''}`}
                onClick={() => setActiveTab('content')}
              >
                My Content
              </button>
              <button
                className={`vd-tab ${activeTab === 'upload' ? 'active' : ''}`}
                onClick={() => setActiveTab('upload')}
              >
                Upload Content
              </button>
              <button
                className={`vd-tab ${activeTab === 'analytics' ? 'active' : ''}`}
                onClick={() => setActiveTab('analytics')}
              >
                Analytics
              </button>
            </nav>

            {/* Tab Content */}
            <div className="vd-tab-content">

              {/* ═══════ VENUE PROFILE TAB ═══════ */}
              {activeTab === 'profile' && (
                <section className="vd-section">
                  <div className="vd-section-header">
                    <h3>Venue Profile</h3>
                    {!isEditingProfile && (
                      <button
                        className="vd-edit-button"
                        onClick={() => setIsEditingProfile(true)}
                      >
                        Edit Profile
                      </button>
                    )}
                  </div>

                  {/* Profile Completeness Bar */}
                  <div className="vd-completeness">
                    <div className="vd-completeness-header">
                      <span>Profile Completeness</span>
                      <span className="vd-completeness-percent">{profileCompleteness.percent}%</span>
                    </div>
                    <div className="vd-completeness-bar">
                      <div
                        className="vd-completeness-fill"
                        style={{ width: `${profileCompleteness.percent}%` }}
                      />
                    </div>
                    {profileCompleteness.missing.length > 0 && (
                      <p className="vd-completeness-hint">
                        Missing: {profileCompleteness.missing.join(', ')}
                      </p>
                    )}
                  </div>

                  {profileMessage && (
                    <div className={`vd-message vd-message-${profileMessage.type}`}>
                      {profileMessage.text}
                    </div>
                  )}

                  {isEditingProfile ? (
                    /* Edit Mode */
                    <div className="vd-profile-form">
                      <div className="vd-form-row">
                        <label>Venue Name</label>
                        <input type="text" value={selectedVenue.name} disabled className="vd-input-disabled" />
                        <span className="vd-field-hint">Contact admin to change venue name</span>
                      </div>
                      <div className="vd-form-row">
                        <label>Address</label>
                        <input
                          type="text"
                          value={profileForm.address}
                          onChange={e => setProfileForm(p => ({ ...p, address: e.target.value }))}
                        />
                      </div>
                      <div className="vd-form-row">
                        <label>City</label>
                        <input
                          type="text"
                          value={profileForm.city}
                          onChange={e => setProfileForm(p => ({ ...p, city: e.target.value }))}
                        />
                      </div>
                      <div className="vd-form-row">
                        <label>Phone</label>
                        <input
                          type="text"
                          value={profileForm.phone}
                          onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                        />
                      </div>
                      <div className="vd-form-row">
                        <label>Website</label>
                        <input
                          type="url"
                          value={profileForm.website}
                          onChange={e => setProfileForm(p => ({ ...p, website: e.target.value }))}
                        />
                      </div>
                      <div className="vd-form-row">
                        <label>Venue Type</label>
                        <select
                          value={profileForm.type}
                          onChange={e => setProfileForm(p => ({ ...p, type: e.target.value }))}
                        >
                          <option value="">Select a type...</option>
                          <option value="American">American</option>
                          <option value="Italian">Italian</option>
                          <option value="Mexican">Mexican</option>
                          <option value="Irish">Irish</option>
                          <option value="Asian">Asian</option>
                          <option value="Seafood">Seafood</option>
                          <option value="Steakhouse">Steakhouse</option>
                          <option value="Pizza">Pizza</option>
                          <option value="BBQ">BBQ</option>
                          <option value="Brewery">Brewery</option>
                          <option value="Bar">Bar</option>
                          <option value="Sports Bar">Sports Bar</option>
                          <option value="Wine Bar">Wine Bar</option>
                          <option value="Gastropub">Gastropub</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      <div className="vd-form-row">
                        <label>Description</label>
                        <textarea
                          value={profileForm.description}
                          onChange={e => setProfileForm(p => ({ ...p, description: e.target.value }))}
                          placeholder="Tell customers about your venue..."
                          rows={4}
                        />
                      </div>
                      <div className="vd-form-row">
                        <label>Facebook URL</label>
                        <input
                          type="url"
                          value={profileForm.facebookUrl}
                          onChange={e => setProfileForm(p => ({ ...p, facebookUrl: e.target.value }))}
                          placeholder="https://www.facebook.com/yourvenue"
                        />
                      </div>
                      <div className="vd-form-row">
                        <label>Instagram URL</label>
                        <input
                          type="url"
                          value={profileForm.instagramUrl}
                          onChange={e => setProfileForm(p => ({ ...p, instagramUrl: e.target.value }))}
                          placeholder="https://www.instagram.com/yourvenue"
                        />
                      </div>
                      <div className="vd-form-actions">
                        <button
                          className="vd-save-button"
                          onClick={handleProfileSave}
                          disabled={profileSaving}
                        >
                          {profileSaving ? 'Saving...' : 'Save Changes'}
                        </button>
                        <button
                          className="vd-cancel-button"
                          onClick={() => {
                            setIsEditingProfile(false);
                            setProfileMessage(null);
                          }}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* View Mode */
                    <div className="vd-profile-view">
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Venue Name</span>
                        <span className="vd-profile-value">{selectedVenue.name}</span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Address</span>
                        <span className="vd-profile-value">{selectedVenue.address || '—'}</span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">City</span>
                        <span className="vd-profile-value">{selectedVenue.city || '—'}</span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Phone</span>
                        <span className="vd-profile-value">{selectedVenue.phone || '—'}</span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Website</span>
                        <span className="vd-profile-value">
                          {selectedVenue.website ? (
                            <a href={selectedVenue.website} target="_blank" rel="noopener noreferrer">
                              {selectedVenue.website}
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Type</span>
                        <span className="vd-profile-value">{selectedVenue.type || '—'}</span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Description</span>
                        <span className="vd-profile-value vd-profile-multiline">{selectedVenue.description || '—'}</span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Facebook</span>
                        <span className="vd-profile-value">
                          {selectedVenue.facebookUrl ? (
                            <a href={selectedVenue.facebookUrl} target="_blank" rel="noopener noreferrer">
                              {selectedVenue.facebookUrl}
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                      <div className="vd-profile-field">
                        <span className="vd-profile-label">Instagram</span>
                        <span className="vd-profile-value">
                          {selectedVenue.instagramUrl ? (
                            <a href={selectedVenue.instagramUrl} target="_blank" rel="noopener noreferrer">
                              {selectedVenue.instagramUrl}
                            </a>
                          ) : '—'}
                        </span>
                      </div>
                    </div>
                  )}

                  {/* ── Plan Cards Row ── */}
                  <div className="vd-plan-row">
                    {/* Your Plan Card */}
                    <div className="vd-plan-card">
                      <div className="vd-plan-header">
                        <h4>Your Plan</h4>
                        <span className={`vd-plan-badge ${isPlatinum ? 'vd-plan-platinum' : isSilverPlus ? 'vd-plan-premium' : 'vd-plan-free'}`}>
                          {isPlatinum ? 'Platinum' : isSilverPlus ? 'Silver' : 'Free'}
                        </span>
                      </div>
                      <div className="vd-plan-features">
                        <div className="vd-plan-feature">
                          <span className="vd-plan-check">&#10003;</span>
                          <span>Venue profile management</span>
                        </div>
                        <div className="vd-plan-feature">
                          <span className="vd-plan-check">&#10003;</span>
                          <span>Content uploads (photo &amp; text)</span>
                        </div>
                        <div className="vd-plan-feature">
                          <span className="vd-plan-check">&#10003;</span>
                          <span>7-day analytics</span>
                        </div>
                        <div className={`vd-plan-feature ${!isSilverPlus ? 'vd-plan-locked' : ''}`}>
                          <span className={isSilverPlus ? 'vd-plan-check' : 'vd-plan-lock'}>
                            {isSilverPlus ? '\u2713' : '\uD83D\uDD12'}
                          </span>
                          <span>Add new content directly</span>
                        </div>
                        <div className={`vd-plan-feature ${!isSilverPlus ? 'vd-plan-locked' : ''}`}>
                          <span className={isSilverPlus ? 'vd-plan-check' : 'vd-plan-lock'}>
                            {isSilverPlus ? '\u2713' : '\uD83D\uDD12'}
                          </span>
                          <span>Phone &amp; social links on listing</span>
                        </div>
                        <div className={`vd-plan-feature ${!isSilverPlus ? 'vd-plan-locked' : ''}`}>
                          <span className={isSilverPlus ? 'vd-plan-check' : 'vd-plan-lock'}>
                            {isSilverPlus ? '\u2713' : '\uD83D\uDD12'}
                          </span>
                          <span>30-day analytics &amp; trends</span>
                        </div>
                        <div className={`vd-plan-feature ${!isSilverPlus ? 'vd-plan-locked' : ''}`}>
                          <span className={isSilverPlus ? 'vd-plan-check' : 'vd-plan-lock'}>
                            {isSilverPlus ? '\u2713' : '\uD83D\uDD12'}
                          </span>
                          <span>Priority review &amp; featured badge</span>
                        </div>
                      </div>
                      {!isSilverPlus && (
                        <button className="vd-upgrade-button" onClick={openUpgradeModal}>
                          Upgrade to Silver
                        </button>
                      )}
                      {isSilverPlus && !isPlatinum && (
                        <p className="vd-plan-active-msg">Silver features are active on your account.</p>
                      )}
                      {isPlatinum && (
                        <p className="vd-plan-active-msg">Platinum features are active on your account.</p>
                      )}
                    </div>

                    {/* Platinum Teaser Card */}
                    {!isPlatinum && (
                      <div className="vd-plan-card vd-plan-teaser">
                        <div className="vd-plan-header">
                          <h4>Platinum</h4>
                          <span className="vd-plan-badge vd-plan-platinum">Platinum</span>
                        </div>
                        <div className="vd-plan-teaser-body">
                          <div className="vd-plan-teaser-blur">
                            <div className="vd-plan-feature">
                              <span className="vd-plan-check">&#10003;</span>
                              <span>Enhanced listing card</span>
                            </div>
                            <div className="vd-plan-feature">
                              <span className="vd-plan-check">&#10003;</span>
                              <span>Reservation link on card</span>
                            </div>
                            <div className="vd-plan-feature">
                              <span className="vd-plan-check">&#10003;</span>
                              <span>Image uploads on all content</span>
                            </div>
                            <div className="vd-plan-feature">
                              <span className="vd-plan-check">&#10003;</span>
                              <span>90-day analytics &amp; trends</span>
                            </div>
                            <div className="vd-plan-feature">
                              <span className="vd-plan-check">&#10003;</span>
                              <span>Competitive benchmarks</span>
                            </div>
                            <div className="vd-plan-feature">
                              <span className="vd-plan-check">&#10003;</span>
                              <span>Audience insights</span>
                            </div>
                          </div>
                          <div className="vd-plan-teaser-banner">
                            <span>Coming This Spring</span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </section>
              )}

              {/* ═══════ MY CONTENT TAB ═══════ */}
              {activeTab === 'content' && (
                <section className="vd-section">
                  <div className="vd-section-header">
                    <h3>My Content</h3>
                    <div className="vd-section-header-actions">
                      {isSilverPlus && (
                        <button
                          className="vd-add-new-button"
                          onClick={openAddNewModal}
                        >
                          + Add New
                        </button>
                      )}
                      <button
                        className="vd-refresh-button"
                        onClick={() => { fetchContent(); fetchSubmissions(); }}
                      >
                        Refresh
                      </button>
                    </div>
                  </div>

                  {contentLoading ? (
                    <div className="vd-loading-inline">
                      <div className="vd-spinner-small"></div>
                      <span>Loading content...</span>
                    </div>
                  ) : (
                    <>
                      {/* Happy Hour Deals */}
                      <div className="vd-content-group">
                        <h4>Happy Hour Deals ({content.deals.length})</h4>
                        {content.deals.length === 0 ? (
                          <p className="vd-empty-text">No happy hour deals yet. Upload content to get started!</p>
                        ) : (
                          <div className="vd-content-cards">
                            {content.deals.map(deal => {
                              const activeReq = getActiveRequest(deal.id);
                              const isPending = activeReq?.status === 'pending';
                              const isRejected = activeReq?.status === 'rejected';
                              const isRemoval = activeReq?.extractedData?.requestType === 'removal_request';
                              return (
                              <div key={deal.id} className="vd-content-card">
                                <div className="vd-card-header-row">
                                  <div className="vd-card-type">Happy Hour</div>
                                  {isPending && <span className="vd-card-pending-badge">{isRemoval ? 'Removal Pending' : 'Edit Pending'}</span>}
                                  {isRejected && <span className="vd-card-rejected-badge">{isRemoval ? 'Removal Rejected' : 'Edit Rejected'}</span>}
                                </div>
                                {isRejected && activeReq.reviewNotes && (
                                  <div className="vd-card-rejection-note">
                                    <strong>Admin:</strong> {activeReq.reviewNotes}
                                  </div>
                                )}
                                <div className="vd-card-title">{deal['Deal Name'] || 'Untitled Deal'}</div>
                                <div className="vd-card-detail">{deal['Days of Week'] || ''}</div>
                                <div className="vd-card-detail">{deal['Start Time'] || ''} – {deal['End Time'] || ''}</div>
                                <div className="vd-card-description">{deal.Description || ''}</div>
                                <div className="vd-card-actions">
                                  <button className="vd-card-action-edit" disabled={isPending} onClick={() => openRequestModal('edit', 'deal', deal.id, deal['Deal Name'] || 'Untitled Deal')}>
                                    Request Edit
                                  </button>
                                  <button className="vd-card-action-remove" disabled={isPending} onClick={() => openRequestModal('removal', 'deal', deal.id, deal['Deal Name'] || 'Untitled Deal')}>
                                    Request Removal
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Events */}
                      <div className="vd-content-group">
                        <h4>Events ({content.events.length})</h4>
                        {content.events.length === 0 ? (
                          <p className="vd-empty-text">No events yet.</p>
                        ) : (
                          <div className="vd-content-cards">
                            {content.events.map(event => {
                              const activeReq = getActiveRequest(event.id);
                              const isPending = activeReq?.status === 'pending';
                              const isRejected = activeReq?.status === 'rejected';
                              const isRemoval = activeReq?.extractedData?.requestType === 'removal_request';
                              return (
                              <div key={event.id} className="vd-content-card">
                                <div className="vd-card-header-row">
                                  <div className="vd-card-type vd-card-type-event">Event</div>
                                  {isPending && <span className="vd-card-pending-badge">{isRemoval ? 'Removal Pending' : 'Edit Pending'}</span>}
                                  {isRejected && <span className="vd-card-rejected-badge">{isRemoval ? 'Removal Rejected' : 'Edit Rejected'}</span>}
                                </div>
                                {isRejected && activeReq.reviewNotes && (
                                  <div className="vd-card-rejection-note">
                                    <strong>Admin:</strong> {activeReq.reviewNotes}
                                  </div>
                                )}
                                <div className="vd-card-title">{event['Event Name'] || 'Untitled Event'}</div>
                                <div className="vd-card-detail">{event['Event Type'] || ''}</div>
                                <div className="vd-card-detail">
                                  {event['Event Start'] ? new Date(event['Event Start']).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                                </div>
                                <div className="vd-card-description">{event.Description || ''}</div>
                                <div className="vd-card-actions">
                                  <button className="vd-card-action-edit" disabled={isPending} onClick={() => openRequestModal('edit', 'event', event.id, event['Event Name'] || 'Untitled Event')}>
                                    Request Edit
                                  </button>
                                  <button className="vd-card-action-remove" disabled={isPending} onClick={() => openRequestModal('removal', 'event', event.id, event['Event Name'] || 'Untitled Event')}>
                                    Request Removal
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Specials */}
                      <div className="vd-content-group">
                        <h4>Restaurant Specials ({content.specials.length})</h4>
                        {content.specials.length === 0 ? (
                          <p className="vd-empty-text">No specials yet.</p>
                        ) : (
                          <div className="vd-content-cards">
                            {content.specials.map(special => {
                              const activeReq = getActiveRequest(special.id);
                              const isPending = activeReq?.status === 'pending';
                              const isRejected = activeReq?.status === 'rejected';
                              const isRemoval = activeReq?.extractedData?.requestType === 'removal_request';
                              return (
                              <div key={special.id} className="vd-content-card">
                                <div className="vd-card-header-row">
                                  <div className="vd-card-type vd-card-type-special">Special</div>
                                  {isPending && <span className="vd-card-pending-badge">{isRemoval ? 'Removal Pending' : 'Edit Pending'}</span>}
                                  {isRejected && <span className="vd-card-rejected-badge">{isRemoval ? 'Removal Rejected' : 'Edit Rejected'}</span>}
                                </div>
                                {isRejected && activeReq.reviewNotes && (
                                  <div className="vd-card-rejection-note">
                                    <strong>Admin:</strong> {activeReq.reviewNotes}
                                  </div>
                                )}
                                <div className="vd-card-title">{special['Special Name'] || 'Untitled Special'}</div>
                                <div className="vd-card-detail">{special['Days of Week'] || ''}</div>
                                <div className="vd-card-description">{special.Description || ''}</div>
                                <div className="vd-card-actions">
                                  <button className="vd-card-action-edit" disabled={isPending} onClick={() => openRequestModal('edit', 'special', special.id, special['Special Name'] || 'Untitled Special')}>
                                    Request Edit
                                  </button>
                                  <button className="vd-card-action-remove" disabled={isPending} onClick={() => openRequestModal('removal', 'special', special.id, special['Special Name'] || 'Untitled Special')}>
                                    Request Removal
                                  </button>
                                </div>
                              </div>
                              );
                            })}
                          </div>
                        )}
                      </div>

                      {/* Pending Submissions */}
                      {submissions.length > 0 && (
                        <div className="vd-content-group">
                          <h4>Submissions ({submissions.length})</h4>
                          <div className="vd-submissions-list">
                            {submissions.map(sub => {
                              const ed = sub.extractedData || {};
                              const isRequest = ed.requestType === 'edit_request' || ed.requestType === 'removal_request';
                              const isAddNew = ed.requestType === 'add_new';
                              const requestLabel = ed.requestType === 'edit_request' ? 'Edit Request'
                                : ed.requestType === 'removal_request' ? 'Removal Request'
                                : ed.requestType === 'add_new' ? 'New Content'
                                : 'Content Upload';
                              const contentLabel = sub.contentType === 'happy_hour' ? 'Happy Hour'
                                : sub.contentType === 'event' ? 'Event'
                                : sub.contentType === 'special' ? 'Special'
                                : sub.contentType || 'Content';
                              const statusIcon = sub.status === 'approved' ? 'green'
                                : sub.status === 'rejected' ? 'red'
                                : sub.status === 'revision_requested' ? 'orange'
                                : 'yellow'; // pending

                              return (
                                <div key={sub.id} className="vd-submission-card">
                                  <div className="vd-submission-header">
                                    <span className={`vd-status-dot vd-status-dot-${statusIcon}`} title={sub.status} />
                                    <span className={`vd-status-badge vd-status-${sub.status}`}>
                                      {sub.status === 'pending' ? 'Pending Review' :
                                       sub.status === 'approved' ? 'Approved' :
                                       sub.status === 'rejected' ? 'Rejected' :
                                       'Revision Requested'}
                                    </span>
                                    <span className="vd-submission-date">
                                      {sub.submittedAt ? new Date(sub.submittedAt).toLocaleDateString() : ''}
                                    </span>
                                  </div>
                                  <div className="vd-submission-detail">
                                    <span className="vd-submission-request-type">{requestLabel}</span>
                                    <span className="vd-submission-type-badge">{contentLabel}</span>
                                    {(isRequest && ed.originalContentName) && (
                                      <span className="vd-submission-content-name">{ed.originalContentName}</span>
                                    )}
                                    {isAddNew && ed.fields && (
                                      <span className="vd-submission-content-name">
                                        {ed.fields['Deal Name'] || ed.fields['Event Name'] || ed.fields['Special Name'] || ''}
                                      </span>
                                    )}
                                    {(!isRequest && !isAddNew && sub.restaurantName) && (
                                      <span className="vd-submission-content-name">{sub.restaurantName}</span>
                                    )}
                                  </div>
                                  {(isRequest || isAddNew) && ed.notes && (
                                    <div className="vd-submission-user-notes">
                                      <strong>Your note:</strong> {ed.notes}
                                    </div>
                                  )}
                                  {sub.reviewNotes && (
                                    <div className="vd-submission-notes">
                                      <strong>Admin Feedback:</strong> {sub.reviewNotes}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </section>
              )}

              {/* ═══════ UPLOAD CONTENT TAB ═══════ */}
              {activeTab === 'upload' && (
                <section className="vd-section">
                  <div className="vd-section-header">
                    <h3>Upload Content for {selectedVenue.name}</h3>
                  </div>

                  {uploadMessage && (
                    <div className={`vd-message vd-message-${uploadMessage.type}`}>
                      {uploadMessage.text}
                    </div>
                  )}

                  {/* Upload Mode Tabs */}
                  <div className="vd-upload-mode-tabs">
                    <button
                      className={`vd-upload-mode-tab ${uploadMode === 'photos' ? 'active' : ''}`}
                      onClick={() => setUploadMode('photos')}
                    >
                      Photo Upload
                    </button>
                    <button
                      className={`vd-upload-mode-tab ${uploadMode === 'text' ? 'active' : ''}`}
                      onClick={() => setUploadMode('text')}
                    >
                      Text Upload
                    </button>
                  </div>

                  {/* ── Photo Upload ── */}
                  {uploadMode === 'photos' && (
                    <div className="vd-upload-section">
                      {!extractedData ? (
                        <>
                          <p className="vd-upload-instructions">
                            Upload photos of menus, specials boards, or event flyers. Our AI will extract deals, events, and specials automatically.
                          </p>

                          <div className="vd-file-input-area">
                            <input
                              type="file"
                              id="vd-photo-upload"
                              accept="image/*"
                              multiple
                              onChange={handleFileSelect}
                              disabled={isProcessingPhotos}
                              className="vd-file-input"
                            />
                            <label htmlFor="vd-photo-upload" className="vd-file-input-label">
                              Choose Photos (max 10)
                            </label>
                          </div>

                          {pendingFiles.length > 0 && (
                            <div className="vd-pending-files">
                              <h5>{pendingFiles.length} file{pendingFiles.length !== 1 ? 's' : ''} selected</h5>
                              <ul className="vd-file-list">
                                {pendingFiles.map((file, i) => (
                                  <li key={i} className="vd-file-item">
                                    <span>{file.name}</span>
                                    <span className="vd-file-size">({(file.size / 1024).toFixed(0)} KB)</span>
                                    <button
                                      className="vd-file-remove"
                                      onClick={() => handleRemoveFile(i)}
                                      title="Remove file"
                                    >
                                      &times;
                                    </button>
                                  </li>
                                ))}
                              </ul>
                              <div className="vd-upload-actions">
                                <button
                                  className="vd-save-button"
                                  onClick={handleProcessPhotos}
                                  disabled={isProcessingPhotos}
                                >
                                  {isProcessingPhotos ? 'Processing...' : 'Process Photos'}
                                </button>
                                <button
                                  className="vd-cancel-button"
                                  onClick={handleClearPhotoUpload}
                                  disabled={isProcessingPhotos}
                                >
                                  Clear
                                </button>
                              </div>
                            </div>
                          )}

                          {isProcessingPhotos && (
                            <div className="vd-loading-inline">
                              <div className="vd-spinner-small"></div>
                              <span>Analyzing photos with AI... This may take a moment.</span>
                            </div>
                          )}
                        </>
                      ) : (
                        renderExtractionReview(
                          extractedData,
                          selectedExtractions,
                          handleToggleExtraction,
                          handleSaveExtractions,
                          handleClearPhotoUpload
                        )
                      )}
                    </div>
                  )}

                  {/* ── Text Upload ── */}
                  {uploadMode === 'text' && (
                    <div className="vd-upload-section">
                      {!textExtractedData ? (
                        <>
                          <p className="vd-upload-instructions">
                            Paste text from social media posts, emails, or websites. Our AI will extract deals, events, and specials automatically.
                          </p>

                          <div className="vd-text-input-area">
                            <textarea
                              value={pastedText}
                              onChange={e => setPastedText(e.target.value)}
                              placeholder={"Example:\nHappy Hour Mon-Fri 3-6pm\n$5 draft beers, $6 house wines\nLive music every Saturday 8pm"}
                              rows={8}
                              disabled={isProcessingText}
                            />
                            <div className="vd-text-char-count">
                              {pastedText.length} characters
                            </div>
                          </div>

                          <div className="vd-upload-actions">
                            <button
                              className="vd-save-button"
                              onClick={handleProcessText}
                              disabled={isProcessingText || !pastedText.trim()}
                            >
                              {isProcessingText ? 'Processing...' : 'Process Text'}
                            </button>
                            {pastedText.trim() && (
                              <button
                                className="vd-cancel-button"
                                onClick={handleClearTextUpload}
                                disabled={isProcessingText}
                              >
                                Clear
                              </button>
                            )}
                          </div>

                          {isProcessingText && (
                            <div className="vd-loading-inline">
                              <div className="vd-spinner-small"></div>
                              <span>Analyzing text with AI... This may take a moment.</span>
                            </div>
                          )}
                        </>
                      ) : (
                        renderExtractionReview(
                          textExtractedData,
                          textSelectedExtractions,
                          handleToggleTextExtraction,
                          handleSaveTextExtractions,
                          handleClearTextUpload
                        )
                      )}
                    </div>
                  )}
                </section>
              )}

              {/* ═══════ ANALYTICS TAB ═══════ */}
              {activeTab === 'analytics' && (
                <section className="vd-section">
                  <div className="vd-section-header">
                    <h3>Analytics</h3>
                    <button className="vd-refresh-button" onClick={() => { fetchAnalytics(); fetchGa4Engagement(); }} disabled={analyticsLoading || ga4EngagementLoading}>
                      {analyticsLoading || ga4EngagementLoading ? 'Loading...' : 'Refresh'}
                    </button>
                  </div>

                  {/* ── ALL TIERS: Content + Engagement Snapshot ── */}
                  <div className="an-stat-grid" style={{ marginBottom: 20 }}>
                    <StatCard label="Happy Hours" value={content.deals.length} icon="🍺" color="#f59e0b" />
                    <StatCard label="Events" value={content.events.length} icon="🎉" color="#ec4899" />
                    <StatCard label="Specials" value={content.specials.length} icon="⭐" color="#10b981" />
                    <StatCard label="Profile" value={`${profileCompleteness.percent}%`} icon="📋" color="#667eea" />
                  </div>

                  {/* GA4 engagement snapshot (all tiers get numbers) */}
                  <div className="an-stat-grid" style={{ marginBottom: 20 }}>
                    <StatCard
                      label="Venue Views"
                      value={ga4Engagement?.totals?.expansions ?? null}
                      icon="👁️"
                      color="#8b5cf6"
                      loading={ga4EngagementLoading}
                      subtitle={ga4Engagement?.tierLimited ? 'Last 7 days' : `Last ${ga4Engagement?.period || '7d'}`}
                    />
                    <StatCard
                      label="Deal/Event Clicks"
                      value={ga4Engagement?.totals ? (ga4Engagement.totals.dealClicks + ga4Engagement.totals.eventClicks) : null}
                      icon="🖱️"
                      color="#06b6d4"
                      loading={ga4EngagementLoading}
                    />
                    <StatCard
                      label="Calendar Adds"
                      value={ga4Engagement?.totals?.calendarAdds ?? null}
                      icon="📅"
                      color="#16a34a"
                      loading={ga4EngagementLoading}
                    />
                  </div>

                  {/* ── BASIC TIER: Upgrade prompt ── */}
                  {!isSilverPlus && (
                    <div className="an-upgrade-prompt">
                      <div className="an-upgrade-icon">📈</div>
                      <div className="an-upgrade-text">
                        Upgrade to Silver for engagement trends, click tracking, day-of-week insights, and more
                      </div>
                      <button className="an-upgrade-btn">Contact Us to Upgrade</button>
                    </div>
                  )}

                  {/* ── SILVER+ TIER: Engagement Trends & Full Analytics ── */}
                  {isSilverPlus && (
                    <div className="vd-analytics-full">
                      {/* 30-day engagement trend chart */}
                      <AnalyticsLineChart
                        title="Engagement Trend (30 Days)"
                        data={ga4Engagement?.daily || []}
                        lines={[
                          { key: 'expansions', label: 'Views', color: '#8b5cf6' },
                          { key: 'total', label: 'Total Engagement', color: '#06b6d4' },
                        ]}
                        loading={ga4EngagementLoading}
                        height={250}
                      />

                      {/* Day-of-week heatmap */}
                      <HeatmapChart
                        title="Engagement by Day of Week"
                        data={ga4Heatmap?.days || []}
                        loading={ga4HeatmapLoading}
                      />

                      {/* Content Freshness (from Airtable) */}
                      {analytics && (
                        <div className="vd-analytics-widget">
                          <h4>Content Freshness</h4>
                          <div className="vd-freshness-info">
                            {analytics.lastContentUpdate ? (
                              <>
                                <div className={`vd-freshness-badge ${analytics.daysSinceUpdate > 30 ? 'stale' : analytics.daysSinceUpdate > 14 ? 'aging' : 'fresh'}`}>
                                  {analytics.daysSinceUpdate === 0 ? 'Updated today' :
                                   analytics.daysSinceUpdate === 1 ? 'Updated yesterday' :
                                   `Updated ${analytics.daysSinceUpdate} days ago`}
                                </div>
                                <p className="vd-freshness-date">
                                  Last content update: {new Date(analytics.lastContentUpdate).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                                </p>
                                {analytics.daysSinceUpdate > 14 && (
                                  <p className="vd-freshness-tip">
                                    Tip: Venues that update content regularly get more engagement. Consider adding new specials or events!
                                  </p>
                                )}
                              </>
                            ) : (
                              <p className="vd-freshness-empty">No content published yet. Upload your first deals or events!</p>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Submission History (from Airtable) */}
                      {analytics && (
                        <div className="vd-analytics-widget">
                          <h4>Submission History</h4>
                          <div className="vd-submission-stats">
                            <div className="vd-sub-stat">
                              <span className="vd-sub-stat-value">{analytics.submissionStats.total}</span>
                              <span className="vd-sub-stat-label">Total</span>
                            </div>
                            <div className="vd-sub-stat vd-sub-stat-approved">
                              <span className="vd-sub-stat-value">{analytics.submissionStats.approved}</span>
                              <span className="vd-sub-stat-label">Approved</span>
                            </div>
                            <div className="vd-sub-stat vd-sub-stat-pending">
                              <span className="vd-sub-stat-value">{analytics.submissionStats.pending}</span>
                              <span className="vd-sub-stat-label">Pending</span>
                            </div>
                            <div className="vd-sub-stat vd-sub-stat-rejected">
                              <span className="vd-sub-stat-value">{analytics.submissionStats.rejected}</span>
                              <span className="vd-sub-stat-label">Rejected</span>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Ad Performance (from Airtable) */}
                      {analytics?.adPerformance ? (
                        <div className="vd-analytics-widget">
                          <h4>Ad Performance</h4>
                          <div className="vd-ad-stats">
                            <div className="vd-ad-stat">
                              <span className="vd-ad-stat-value">{analytics.adPerformance.activeCampaigns}</span>
                              <span className="vd-ad-stat-label">Active Campaigns</span>
                            </div>
                            <div className="vd-ad-stat">
                              <span className="vd-ad-stat-value">{(analytics.adPerformance.totalImpressions || 0).toLocaleString()}</span>
                              <span className="vd-ad-stat-label">Impressions</span>
                            </div>
                            <div className="vd-ad-stat">
                              <span className="vd-ad-stat-value">{(analytics.adPerformance.totalClicks || 0).toLocaleString()}</span>
                              <span className="vd-ad-stat-label">Clicks</span>
                            </div>
                            <div className="vd-ad-stat">
                              <span className="vd-ad-stat-value">
                                {analytics.adPerformance.totalImpressions > 0
                                  ? `${((analytics.adPerformance.totalClicks / analytics.adPerformance.totalImpressions) * 100).toFixed(1)}%`
                                  : '—'}
                              </span>
                              <span className="vd-ad-stat-label">CTR</span>
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {/* Platinum upgrade prompt (for Silver users) */}
                      {!isPlatinum && (
                        <div className="an-upgrade-prompt" style={{ marginTop: 16 }}>
                          <div className="an-upgrade-icon">🏆</div>
                          <div className="an-upgrade-text">
                            Upgrade to Platinum for 90-day trends, competitive benchmarks, audience insights, and CSV export
                          </div>
                          <button className="an-upgrade-btn">Contact Us to Upgrade</button>
                        </div>
                      )}
                    </div>
                  )}
                </section>
              )}
            </div>
          </>
        )}
      </div>

      {/* Request Edit/Removal Modal */}
      {requestModal && (
        <div className="vd-modal-overlay" onClick={closeRequestModal}>
          <div className={`vd-modal ${requestModal.type === 'edit' ? 'vd-modal-wide' : ''}`} onClick={e => e.stopPropagation()}>
            <div className="vd-modal-header">
              <h3>{requestModal.type === 'edit' ? 'Edit Content' : 'Request Removal'}</h3>
              <button className="vd-modal-close" onClick={closeRequestModal}>&times;</button>
            </div>

            <div className="vd-modal-body">
              <div className="vd-modal-content-info">
                <span className="vd-modal-content-label">
                  {requestModal.contentType === 'deal' ? 'Happy Hour' :
                   requestModal.contentType === 'event' ? 'Event' : 'Special'}
                </span>
                <span className="vd-modal-content-name">{requestModal.contentName}</span>
              </div>

              {requestModal.type === 'edit' ? (
                <div className="vd-edit-form">
                  {/* Deal fields */}
                  {requestModal.contentType === 'deal' && (
                    <>
                      <div className={`vd-edit-field ${editFormData['Deal Name'] !== originalFormData['Deal Name'] ? 'vd-field-changed' : ''}`}>
                        <label>Deal Name</label>
                        <input
                          type="text"
                          value={editFormData['Deal Name'] || ''}
                          onChange={e => setEditFormData(prev => ({ ...prev, 'Deal Name': e.target.value }))}
                        />
                      </div>
                      <div className="vd-time-row">
                        <div className={`vd-edit-field ${editFormData['Start Time'] !== originalFormData['Start Time'] ? 'vd-field-changed' : ''}`}>
                          <label>Start Time</label>
                          <select
                            value={editFormData['Start Time'] || ''}
                            onChange={e => setEditFormData(prev => ({ ...prev, 'Start Time': e.target.value }))}
                          >
                            <option value="">Select...</option>
                            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className={`vd-edit-field ${editFormData['End Time'] !== originalFormData['End Time'] ? 'vd-field-changed' : ''}`}>
                          <label>End Time</label>
                          <select
                            value={editFormData['End Time'] || ''}
                            onChange={e => setEditFormData(prev => ({ ...prev, 'End Time': e.target.value }))}
                          >
                            <option value="">Select...</option>
                            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className={`vd-edit-field ${JSON.stringify(editFormData['Days of Week']) !== JSON.stringify(originalFormData['Days of Week']) ? 'vd-field-changed' : ''}`}>
                        <label>Days of Week</label>
                        <div className="vd-days-group">
                          {ALL_DAYS.map(day => (
                            <label key={day} className="vd-day-checkbox">
                              <input
                                type="checkbox"
                                checked={(editFormData['Days of Week'] || []).includes(day)}
                                onChange={e => {
                                  const days = editFormData['Days of Week'] || [];
                                  setEditFormData(prev => ({
                                    ...prev,
                                    'Days of Week': e.target.checked
                                      ? [...days, day]
                                      : days.filter(d => d !== day),
                                  }));
                                }}
                              />
                              {day.slice(0, 3)}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className={`vd-edit-field ${editFormData['Description'] !== originalFormData['Description'] ? 'vd-field-changed' : ''}`}>
                        <label>Description</label>
                        <textarea
                          rows={3}
                          value={editFormData['Description'] || ''}
                          onChange={e => setEditFormData(prev => ({ ...prev, 'Description': e.target.value }))}
                        />
                      </div>
                    </>
                  )}

                  {/* Event fields */}
                  {requestModal.contentType === 'event' && (
                    <>
                      <div className={`vd-edit-field ${editFormData['Event Name'] !== originalFormData['Event Name'] ? 'vd-field-changed' : ''}`}>
                        <label>Event Name</label>
                        <input
                          type="text"
                          value={editFormData['Event Name'] || ''}
                          onChange={e => setEditFormData(prev => ({ ...prev, 'Event Name': e.target.value }))}
                        />
                      </div>
                      <div className={`vd-edit-field ${editFormData['Event Type'] !== originalFormData['Event Type'] ? 'vd-field-changed' : ''}`}>
                        <label>Event Type</label>
                        <select
                          value={editFormData['Event Type'] || ''}
                          onChange={e => setEditFormData(prev => ({ ...prev, 'Event Type': e.target.value }))}
                        >
                          <option value="">Select...</option>
                          {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className={`vd-edit-field ${editFormData['Recurring'] !== originalFormData['Recurring'] ? 'vd-field-changed' : ''}`}>
                        <label className="vd-day-checkbox" style={{ fontWeight: 600, color: '#555', fontSize: '0.85rem' }}>
                          <input
                            type="checkbox"
                            checked={!!editFormData['Recurring']}
                            onChange={e => setEditFormData(prev => ({ ...prev, 'Recurring': e.target.checked }))}
                          />
                          Recurring event
                        </label>
                      </div>
                      {editFormData['Recurring'] ? (
                        <>
                          <div className={`vd-edit-field ${JSON.stringify(editFormData['Days of Week']) !== JSON.stringify(originalFormData['Days of Week']) ? 'vd-field-changed' : ''}`}>
                            <label>Days of Week</label>
                            <div className="vd-days-group">
                              {ALL_DAYS.map(day => (
                                <label key={day} className="vd-day-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={(editFormData['Days of Week'] || []).includes(day)}
                                    onChange={e => {
                                      const days = editFormData['Days of Week'] || [];
                                      setEditFormData(prev => ({
                                        ...prev,
                                        'Days of Week': e.target.checked
                                          ? [...days, day]
                                          : days.filter(d => d !== day),
                                      }));
                                    }}
                                  />
                                  {day.slice(0, 3)}
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="vd-time-row">
                            <div className={`vd-edit-field ${editFormData['Start Time'] !== originalFormData['Start Time'] ? 'vd-field-changed' : ''}`}>
                              <label>Start Time</label>
                              <select
                                value={editFormData['Start Time'] || ''}
                                onChange={e => setEditFormData(prev => ({ ...prev, 'Start Time': e.target.value }))}
                              >
                                <option value="">Select...</option>
                                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                            <div className={`vd-edit-field ${editFormData['End Time'] !== originalFormData['End Time'] ? 'vd-field-changed' : ''}`}>
                              <label>End Time</label>
                              <select
                                value={editFormData['End Time'] || ''}
                                onChange={e => setEditFormData(prev => ({ ...prev, 'End Time': e.target.value }))}
                              >
                                <option value="">Select...</option>
                                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className={`vd-edit-field ${editFormData['Event Start'] !== originalFormData['Event Start'] ? 'vd-field-changed' : ''}`}>
                          <label>Event Date & Time</label>
                          <input
                            type="datetime-local"
                            value={editFormData['Event Start'] || ''}
                            onChange={e => setEditFormData(prev => ({ ...prev, 'Event Start': e.target.value }))}
                          />
                        </div>
                      )}
                      <div className={`vd-edit-field ${editFormData['Description'] !== originalFormData['Description'] ? 'vd-field-changed' : ''}`}>
                        <label>Description</label>
                        <textarea
                          rows={3}
                          value={editFormData['Description'] || ''}
                          onChange={e => setEditFormData(prev => ({ ...prev, 'Description': e.target.value }))}
                        />
                      </div>
                    </>
                  )}

                  {/* Special fields */}
                  {requestModal.contentType === 'special' && (
                    <>
                      <div className={`vd-edit-field ${editFormData['Special Name'] !== originalFormData['Special Name'] ? 'vd-field-changed' : ''}`}>
                        <label>Special Name</label>
                        <input
                          type="text"
                          value={editFormData['Special Name'] || ''}
                          onChange={e => setEditFormData(prev => ({ ...prev, 'Special Name': e.target.value }))}
                        />
                      </div>
                      <div className={`vd-edit-field ${JSON.stringify(editFormData['Days of Week']) !== JSON.stringify(originalFormData['Days of Week']) ? 'vd-field-changed' : ''}`}>
                        <label>Days of Week</label>
                        <div className="vd-days-group">
                          {ALL_DAYS.map(day => (
                            <label key={day} className="vd-day-checkbox">
                              <input
                                type="checkbox"
                                checked={(editFormData['Days of Week'] || []).includes(day)}
                                onChange={e => {
                                  const days = editFormData['Days of Week'] || [];
                                  setEditFormData(prev => ({
                                    ...prev,
                                    'Days of Week': e.target.checked
                                      ? [...days, day]
                                      : days.filter(d => d !== day),
                                  }));
                                }}
                              />
                              {day.slice(0, 3)}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className={`vd-edit-field ${editFormData['Description'] !== originalFormData['Description'] ? 'vd-field-changed' : ''}`}>
                        <label>Description</label>
                        <textarea
                          rows={3}
                          value={editFormData['Description'] || ''}
                          onChange={e => setEditFormData(prev => ({ ...prev, 'Description': e.target.value }))}
                        />
                      </div>
                    </>
                  )}

                  {/* Optional notes */}
                  <div className="vd-edit-notes-section">
                    <div className="vd-edit-field">
                      <label>Additional Notes (optional)</label>
                      <textarea
                        rows={2}
                        value={requestNotes}
                        onChange={e => setRequestNotes(e.target.value)}
                        placeholder="Any extra context for the admin..."
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <p className="vd-modal-removal-text">
                  This will send a request to remove this listing. An admin will review and process it.
                </p>
              )}

              {requestMessage && (
                <div className={`vd-message vd-message-${requestMessage.type}`}>
                  {requestMessage.text}
                </div>
              )}
            </div>

            <div className="vd-modal-actions">
              <button
                className={`vd-modal-submit ${requestModal.type === 'removal' ? 'vd-modal-submit-danger' : ''}`}
                onClick={handleSubmitRequest}
                disabled={requestSubmitting}
              >
                {requestSubmitting ? 'Submitting...' :
                 requestModal.type === 'edit' ? 'Submit Edit Request' : 'Submit Removal Request'}
              </button>
              <button className="vd-modal-cancel" onClick={closeRequestModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add New Content Modal (Silver+ only) */}
      {addNewModal && (
        <div className="vd-modal-overlay" onClick={closeAddNewModal}>
          <div className="vd-modal vd-modal-wide" onClick={e => e.stopPropagation()}>
            <div className="vd-modal-header">
              <h3>{addNewContentType ? `Add New ${addNewContentType === 'deal' ? 'Happy Hour Deal' : addNewContentType === 'event' ? 'Event' : 'Special'}` : 'Add New Content'}</h3>
              <button className="vd-modal-close" onClick={closeAddNewModal}>&times;</button>
            </div>

            <div className="vd-modal-body">
              {/* Step 1: Content type picker */}
              {!addNewContentType ? (
                <div className="vd-add-new-picker">
                  <p className="vd-add-new-picker-label">What would you like to add?</p>
                  <div className="vd-add-new-picker-options">
                    <button className="vd-add-new-picker-option" onClick={() => selectAddNewContentType('deal')}>
                      <span className="vd-add-new-picker-icon">🍺</span>
                      <span className="vd-add-new-picker-text">Happy Hour Deal</span>
                    </button>
                    <button className="vd-add-new-picker-option vd-add-new-picker-event" onClick={() => selectAddNewContentType('event')}>
                      <span className="vd-add-new-picker-icon">🎉</span>
                      <span className="vd-add-new-picker-text">Event</span>
                    </button>
                    <button className="vd-add-new-picker-option vd-add-new-picker-special" onClick={() => selectAddNewContentType('special')}>
                      <span className="vd-add-new-picker-icon">⭐</span>
                      <span className="vd-add-new-picker-text">Special</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="vd-edit-form">
                  {/* Back to picker */}
                  <button className="vd-add-new-back" onClick={() => { setAddNewContentType(null); setAddNewMessage(null); setAddNewImageFile(null); }}>
                    ← Change content type
                  </button>

                  {/* Deal fields */}
                  {addNewContentType === 'deal' && (
                    <>
                      <div className="vd-edit-field">
                        <label>Deal Name *</label>
                        <input
                          type="text"
                          value={addNewFormData['Deal Name'] || ''}
                          onChange={e => setAddNewFormData(prev => ({ ...prev, 'Deal Name': e.target.value }))}
                          placeholder="e.g. Half-Price Apps"
                        />
                      </div>
                      <div className="vd-time-row">
                        <div className="vd-edit-field">
                          <label>Start Time</label>
                          <select
                            value={addNewFormData['Start Time'] || ''}
                            onChange={e => setAddNewFormData(prev => ({ ...prev, 'Start Time': e.target.value }))}
                          >
                            <option value="">Select...</option>
                            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                        <div className="vd-edit-field">
                          <label>End Time</label>
                          <select
                            value={addNewFormData['End Time'] || ''}
                            onChange={e => setAddNewFormData(prev => ({ ...prev, 'End Time': e.target.value }))}
                          >
                            <option value="">Select...</option>
                            {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                          </select>
                        </div>
                      </div>
                      <div className="vd-edit-field">
                        <label>Days of Week</label>
                        <div className="vd-days-group">
                          {ALL_DAYS.map(day => (
                            <label key={day} className="vd-day-checkbox">
                              <input
                                type="checkbox"
                                checked={(addNewFormData['Days of Week'] || []).includes(day)}
                                onChange={e => {
                                  const days = addNewFormData['Days of Week'] || [];
                                  setAddNewFormData(prev => ({
                                    ...prev,
                                    'Days of Week': e.target.checked
                                      ? [...days, day]
                                      : days.filter(d => d !== day),
                                  }));
                                }}
                              />
                              {day.slice(0, 3)}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="vd-edit-field">
                        <label>Description</label>
                        <textarea
                          rows={3}
                          value={addNewFormData['Description'] || ''}
                          onChange={e => setAddNewFormData(prev => ({ ...prev, 'Description': e.target.value }))}
                          placeholder="e.g. $5 draft beers, $7 house wines, half-price appetizers"
                        />
                      </div>
                    </>
                  )}

                  {/* Event fields */}
                  {addNewContentType === 'event' && (
                    <>
                      <div className="vd-edit-field">
                        <label>Event Name *</label>
                        <input
                          type="text"
                          value={addNewFormData['Event Name'] || ''}
                          onChange={e => setAddNewFormData(prev => ({ ...prev, 'Event Name': e.target.value }))}
                          placeholder="e.g. Live Music with The Band"
                        />
                      </div>
                      <div className="vd-edit-field">
                        <label>Event Type</label>
                        <select
                          value={addNewFormData['Event Type'] || ''}
                          onChange={e => setAddNewFormData(prev => ({ ...prev, 'Event Type': e.target.value }))}
                        >
                          <option value="">Select...</option>
                          {EVENT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div className="vd-edit-field">
                        <label className="vd-day-checkbox" style={{ fontWeight: 600, color: '#555', fontSize: '0.85rem' }}>
                          <input
                            type="checkbox"
                            checked={!!addNewFormData['Recurring']}
                            onChange={e => setAddNewFormData(prev => ({ ...prev, 'Recurring': e.target.checked }))}
                          />
                          Recurring event
                        </label>
                      </div>
                      {addNewFormData['Recurring'] ? (
                        <>
                          <div className="vd-edit-field">
                            <label>Days of Week</label>
                            <div className="vd-days-group">
                              {ALL_DAYS.map(day => (
                                <label key={day} className="vd-day-checkbox">
                                  <input
                                    type="checkbox"
                                    checked={(addNewFormData['Days of Week'] || []).includes(day)}
                                    onChange={e => {
                                      const days = addNewFormData['Days of Week'] || [];
                                      setAddNewFormData(prev => ({
                                        ...prev,
                                        'Days of Week': e.target.checked
                                          ? [...days, day]
                                          : days.filter(d => d !== day),
                                      }));
                                    }}
                                  />
                                  {day.slice(0, 3)}
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="vd-time-row">
                            <div className="vd-edit-field">
                              <label>Start Time</label>
                              <select
                                value={addNewFormData['Start Time'] || ''}
                                onChange={e => setAddNewFormData(prev => ({ ...prev, 'Start Time': e.target.value }))}
                              >
                                <option value="">Select...</option>
                                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                            <div className="vd-edit-field">
                              <label>End Time</label>
                              <select
                                value={addNewFormData['End Time'] || ''}
                                onChange={e => setAddNewFormData(prev => ({ ...prev, 'End Time': e.target.value }))}
                              >
                                <option value="">Select...</option>
                                {TIME_OPTIONS.map(t => <option key={t} value={t}>{t}</option>)}
                              </select>
                            </div>
                          </div>
                        </>
                      ) : (
                        <div className="vd-edit-field">
                          <label>Event Date & Time</label>
                          <input
                            type="datetime-local"
                            value={addNewFormData['Event Start'] || ''}
                            onChange={e => setAddNewFormData(prev => ({ ...prev, 'Event Start': e.target.value }))}
                          />
                        </div>
                      )}
                      <div className="vd-edit-field">
                        <label>Description</label>
                        <textarea
                          rows={3}
                          value={addNewFormData['Description'] || ''}
                          onChange={e => setAddNewFormData(prev => ({ ...prev, 'Description': e.target.value }))}
                          placeholder="Describe the event..."
                        />
                      </div>
                      {/* Optional event image upload (Silver+ only) */}
                      <div className="vd-edit-field">
                        <label>Event Flyer/Image (optional)</label>
                        <div className="vd-add-new-image-upload">
                          {addNewImageFile ? (
                            <div className="vd-add-new-image-preview">
                              <img src={URL.createObjectURL(addNewImageFile)} alt="Preview" />
                              <button className="vd-file-remove" onClick={() => setAddNewImageFile(null)} title="Remove">&times;</button>
                            </div>
                          ) : (
                            <>
                              <input
                                type="file"
                                id="add-new-image"
                                accept="image/jpeg,image/png,image/webp"
                                onChange={e => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    if (file.size > 5 * 1024 * 1024) {
                                      setAddNewMessage({ type: 'error', text: 'Image must be under 5MB.' });
                                      return;
                                    }
                                    setAddNewImageFile(file);
                                  }
                                  e.target.value = '';
                                }}
                                className="vd-file-input"
                              />
                              <label htmlFor="add-new-image" className="vd-add-new-image-label">
                                Choose Image
                              </label>
                              <span className="vd-field-hint">JPG, PNG, or WebP. Max 5MB.</span>
                            </>
                          )}
                        </div>
                      </div>
                    </>
                  )}

                  {/* Special fields */}
                  {addNewContentType === 'special' && (
                    <>
                      <div className="vd-edit-field">
                        <label>Special Name *</label>
                        <input
                          type="text"
                          value={addNewFormData['Special Name'] || ''}
                          onChange={e => setAddNewFormData(prev => ({ ...prev, 'Special Name': e.target.value }))}
                          placeholder="e.g. Taco Tuesday"
                        />
                      </div>
                      <div className="vd-edit-field">
                        <label>Days of Week</label>
                        <div className="vd-days-group">
                          {ALL_DAYS.map(day => (
                            <label key={day} className="vd-day-checkbox">
                              <input
                                type="checkbox"
                                checked={(addNewFormData['Days of Week'] || []).includes(day)}
                                onChange={e => {
                                  const days = addNewFormData['Days of Week'] || [];
                                  setAddNewFormData(prev => ({
                                    ...prev,
                                    'Days of Week': e.target.checked
                                      ? [...days, day]
                                      : days.filter(d => d !== day),
                                  }));
                                }}
                              />
                              {day.slice(0, 3)}
                            </label>
                          ))}
                        </div>
                      </div>
                      <div className="vd-edit-field">
                        <label>Description</label>
                        <textarea
                          rows={3}
                          value={addNewFormData['Description'] || ''}
                          onChange={e => setAddNewFormData(prev => ({ ...prev, 'Description': e.target.value }))}
                          placeholder="Describe the special..."
                        />
                      </div>
                    </>
                  )}

                  {/* Optional notes */}
                  <div className="vd-edit-notes-section">
                    <div className="vd-edit-field">
                      <label>Notes for Admin (optional)</label>
                      <textarea
                        rows={2}
                        value={addNewNotes}
                        onChange={e => setAddNewNotes(e.target.value)}
                        placeholder="Any extra context..."
                      />
                    </div>
                  </div>
                </div>
              )}

              {addNewMessage && (
                <div className={`vd-message vd-message-${addNewMessage.type}`} style={{ marginTop: '0.75rem' }}>
                  {addNewMessage.text}
                </div>
              )}
            </div>

            {addNewContentType && (
              <div className="vd-modal-actions">
                <button
                  className="vd-modal-submit"
                  onClick={handleAddNewSubmit}
                  disabled={addNewSubmitting}
                >
                  {addNewSubmitting ? 'Submitting...' : 'Submit for Review'}
                </button>
                <button className="vd-modal-cancel" onClick={closeAddNewModal}>
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ═══════ UPGRADE REQUEST MODAL ═══════ */}
      {upgradeModal && (
        <div className="vd-modal-overlay" onClick={closeUpgradeModal}>
          <div className="vd-modal vd-upgrade-modal" onClick={e => e.stopPropagation()}>
            <div className="vd-modal-header">
              <h3>Upgrade to Silver</h3>
              <button className="vd-modal-close" onClick={closeUpgradeModal}>&times;</button>
            </div>

            <div className="vd-modal-body">
              <div className="vd-upgrade-info">
                <div className="vd-upgrade-tier-change">
                  <span className="vd-upgrade-tier-from">Free</span>
                  <span className="vd-upgrade-arrow">&rarr;</span>
                  <span className="vd-upgrade-tier-to">Silver</span>
                </div>
                <p className="vd-upgrade-venue">for <strong>{selectedVenue?.fields?.Name || 'your venue'}</strong></p>
              </div>

              <div className="vd-upgrade-benefits">
                <p className="vd-upgrade-benefits-title">Silver includes:</p>
                <ul>
                  <li>Add new content directly from your dashboard</li>
                  <li>Phone &amp; social links on your listing</li>
                  <li>30-day analytics &amp; engagement trends</li>
                  <li>Priority review &amp; featured badge</li>
                </ul>
              </div>

              <div className="vd-edit-field">
                <label>Message (optional)</label>
                <textarea
                  rows={2}
                  value={upgradeMessage}
                  onChange={e => setUpgradeMessage(e.target.value)}
                  placeholder="Anything you'd like us to know..."
                />
              </div>

              {upgradeResult && (
                <div className={`vd-message vd-message-${upgradeResult.type}`} style={{ marginTop: '0.75rem' }}>
                  {upgradeResult.text}
                </div>
              )}
            </div>

            <div className="vd-modal-actions">
              <button
                className="vd-modal-submit vd-upgrade-submit"
                onClick={handleUpgradeSubmit}
                disabled={upgradeSubmitting}
              >
                {upgradeSubmitting ? 'Submitting...' : 'Request Upgrade'}
              </button>
              <button className="vd-modal-cancel" onClick={closeUpgradeModal}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default VenueOwnerDashboard;
